import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { trapTab } from '../ui/focus';
import './settings.css';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded } from '../ui/unsaved';

/**
 * A settings list opened from the gear at its place of use (rule 9 of docs/wyglad.md), on top of the form, so a
 * half-filled wizard is not lost. Closes with Escape (`DialogLayer`) or "Zamknij"; focus goes back to where it was.
 */
/** `allSettingsLink={false}`: settings kept elsewhere than "Ustawienia firmy" (e.g. the transport price list). */
export function SettingsDialog({ title, onClose, children, allSettingsLink = true }: { title: string; onClose: () => void; children: ReactNode; allSettingsLink?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('h2')?.focus();
  }, []);
  return createPortal(
    <div className="dialog-backdrop" onPointerDown={(e) => e.target === e.currentTarget && leaveGuarded(onClose, e.currentTarget)}>
      <div
        ref={ref}
        className="dialog panel settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <div className="settings-dialog-head">
          <h2 tabIndex={-1}>{title}</h2>
          <DialogLayer onClose={onClose} />
          <button className="btn small ghost" type="button" onClick={() => leaveGuarded(onClose, ref.current)}>
            Zamknij
          </button>
        </div>
        <div className="settings-dialog-body">{children}</div>
        {allSettingsLink && (
          <p className="settings-dialog-foot">
            <Link to="/ustawienia" onClick={onClose}>
              Wszystkie ustawienia firmy
            </Link>
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
